import { GTM } from '../constants/gtm';

interface CustomParams {
  [key: string]: unknown;
}

interface ControlParams {
  groups?: string | string[];
  send_to?: string | string[];
  event_callback?: () => void;
  event_timeout?: number;
}

type EventNames =
  | 'add_payment_info'
  | 'add_to_cart'
  | 'add_to_wishlist'
  | 'begin_checkout'
  | 'checkout_progress'
  | 'exception'
  | 'generate_lead'
  | 'login'
  | 'page_view'
  | 'purchase'
  | 'refund'
  | 'remove_from_cart'
  | 'screen_view'
  | 'search'
  | 'select_content'
  | 'set_checkout_option'
  | 'share'
  | 'sign_up'
  | 'timing_complete'
  | 'view_item'
  | 'view_item_list'
  | 'view_promotion'
  | 'view_search_results';

interface EventParams {
  checkout_option?: string;
  checkout_step?: number;
  content_id?: string;
  content_type?: string;
  coupon?: string;
  currency?: string;
  description?: string;
  fatal?: boolean;
  items?: Item[];
  method?: string;
  number?: string;
  promotions?: Promotion[];
  screen_name?: string;
  search_term?: string;
  shipping?: Currency;
  tax?: Currency;
  transaction_id?: string;
  value?: number;
  event_label?: string;
  event_category?: string;
}

type Currency = string | number;

interface Item {
  brand?: string;
  category?: string;
  creative_name?: string;
  creative_slot?: string;
  id?: string;
  location_id?: string;
  name?: string;
  price?: Currency;
  quantity?: number;
}

interface Promotion {
  creative_name?: string;
  creative_slot?: string;
  id?: string;
  name?: string;
}
interface Gtag {
  (command: 'config', targetId: string, config?: ControlParams | EventParams | CustomParams): void;
  (command: 'set', config: CustomParams): void;
  (command: 'js', config: Date): void;
  (
    command: 'event',
    eventName: EventNames | string,
    eventParams?: ControlParams | EventParams | CustomParams
  ): void;
}

declare global {
  interface Window {
    gtag: Gtag;
  }
}

// The optional call belongs on `gtag`, not on `window`. `gtag` is defined by the
// inline GTM snippet (see GtmScript), so on the web it is always there — but the
// native build does not load that snippet, while GTM.id still comes from the shared
// .env and stays truthy. Without `?.` every route change would throw here.
export const tracking = {
  // https://developers.google.com/analytics/devguides/collection/gtagjs/pages
  pageView: (url: string) => {
    if (GTM.id) {
      window?.gtag?.('config', GTM.id, {
        page_path: url,
      });
    }
  },
  // https://developers.google.com/analytics/devguides/collection/gtagjs/events
  event: ({
    action,
    category,
    label,
    value,
  }: {
    action: string;
    category?: string;
    label?: string;
    value?: string;
  }) => {
    try {
      window?.gtag?.('event', action, {
        event_category: category,
        event_label: label,
        value,
      });
    } catch (error) {
      console.error('Error tracking event', error);
    }
  },
};
